import {
	createFileRoute,
	Link,
	Outlet,
	useMatches,
} from "@tanstack/react-router";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";
import { formatSeoTitle } from "@/lib/seo-meta";

type HelpSlug =
	| "quick-start"
	| "creating-posts"
	| "editing-posts"
	| "deleting-posts"
	| "scheduling-posts"
	| "calendar"
	| "inviting-members"
	| "roles"
	| "mcp"
	| "mcp-tools"
	| "connecting-accounts";

type HelpItem = {
	slug: HelpSlug;
	label: string;
};

type HelpSection = {
	group: string;
	items: HelpItem[];
};

const helpSeoByPath: Record<string, { title: string; description: string }> = {
	"/docs": {
		title: "AgentReacher | Docs",
		description:
			"Learn how to use AgentReacher. Create posts, schedule content, invite team members, and connect your AI to social media.",
	},
	"/docs/quick-start": {
		title: "Quick Start - AgentReacher Docs",
		description:
			"Set up AgentReacher, connect social accounts, and publish your first AI-assisted social post.",
	},
	"/docs/creating-posts": {
		title: "Creating Posts - AgentReacher Docs",
		description:
			"Create social posts in AgentReacher for every connected platform.",
	},
	"/docs/editing-posts": {
		title: "Editing Posts - AgentReacher Docs",
		description:
			"Edit drafts, scheduled posts, and platform-specific social content.",
	},
	"/docs/deleting-posts": {
		title: "Deleting Posts - AgentReacher Docs",
		description: "Delete drafts and scheduled social posts in AgentReacher.",
	},
	"/docs/scheduling-posts": {
		title: "Scheduling Posts - AgentReacher Docs",
		description:
			"Schedule social posts across connected platforms from AgentReacher.",
	},
	"/docs/calendar": {
		title: "Using the Calendar - AgentReacher Docs",
		description:
			"Use the AgentReacher calendar to review, plan, and manage scheduled posts.",
	},
	"/docs/inviting-members": {
		title: "Inviting Members - AgentReacher Docs",
		description:
			"Invite teammates to an AgentReacher workspace and collaborate on publishing.",
	},
	"/docs/roles": {
		title: "Roles and Permissions - AgentReacher Docs",
		description:
			"Understand AgentReacher workspace roles and access permissions.",
	},
	"/docs/mcp": {
		title: "MCP Overview - AgentReacher Docs",
		description:
			"Learn how AgentReacher uses MCP to let AI assistants control social media tools.",
	},
	"/docs/mcp-tools": {
		title: "MCP Tools - AgentReacher Docs",
		description:
			"Reference the MCP tools AgentReacher exposes for social accounts, posts, scheduling, and workspaces.",
	},
	"/docs/connecting-accounts": {
		title: "Connecting Accounts - AgentReacher Docs",
		description:
			"Connect Instagram, TikTok, YouTube, X, LinkedIn, Facebook, Bluesky, Threads, and Pinterest accounts.",
	},
};

export const Route = createFileRoute("/docs")({
	component: HelpLayout,
	head: ({ matches }) => {
		const currentPath =
			(matches.at(-1) as { fullPath?: string } | undefined)?.fullPath ??
			"/docs";
		const internalPath = currentPath === "/docs/" ? "/docs" : currentPath;
		const seo = helpSeoByPath[internalPath] ?? helpSeoByPath["/docs"];
		const title = formatSeoTitle(seo.title);
		const seoUrls = getLocalizedSeoUrls({
			matches,
			internalPath,
		});

		return {
			meta: [
				{ title },
				{
					name: "description",
					content: seo.description,
				},
				{ property: "og:title", content: title },
				{
					property: "og:description",
					content: seo.description,
				},
				{
					property: "og:image",
					content: ogImageUrl("help"),
				},
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:title", content: title },
				{ name: "twitter:description", content: seo.description },
				{ name: "twitter:image", content: ogImageUrl("help") },
			],
			links: buildLocalizedSeoLinks({
				matches,
				internalPath,
			}),
		};
	},
});

export const helpSections: HelpSection[] = [
	{
		group: "Getting started",
		items: [{ slug: "quick-start", label: "Quick start" }],
	},
	{
		group: "Posts",
		items: [
			{ slug: "creating-posts", label: "Creating posts" },
			{ slug: "editing-posts", label: "Editing posts" },
			{ slug: "deleting-posts", label: "Deleting posts" },
		],
	},
	{
		group: "Scheduling",
		items: [
			{ slug: "scheduling-posts", label: "Scheduling posts" },
			{ slug: "calendar", label: "Using the calendar" },
		],
	},
	{
		group: "Team",
		items: [
			{ slug: "inviting-members", label: "Inviting members" },
			{ slug: "roles", label: "Roles and permissions" },
		],
	},
	{
		group: "Integrations",
		items: [
			{ slug: "connecting-accounts", label: "Connecting accounts" },
			{ slug: "mcp", label: "MCP overview" },
			{ slug: "mcp-tools", label: "MCP tools reference" },
		],
	},
] as const;

function Sidebar({ currentSlug }: { currentSlug: string | null }) {
	return (
		<nav className="space-y-5">
			{helpSections.map((section) => (
				<div key={section.group}>
					<p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
						{section.group}
					</p>
					<ul className="space-y-0.5">
						{section.items.map((item) => (
							<li key={item.slug}>
								<Link
									to={`/docs/${item.slug}`}
									className={`block text-sm py-1 transition-colors ${
										currentSlug === item.slug
											? "text-primary font-medium"
											: "text-muted-foreground hover:text-foreground"
									}`}
								>
									{item.label}
								</Link>
							</li>
						))}
					</ul>
				</div>
			))}
		</nav>
	);
}

function HelpLayout() {
	const matches = useMatches();
	const lastMatch = matches[matches.length - 1];
	const currentSlug = lastMatch?.fullPath.replace("/docs/", "") || null;
	const currentLabel =
		helpSections
			.flatMap((section) => section.items)
			.find((item) => item.slug === currentSlug)?.label ?? "Browse topics";

	return (
		<div className="relative flex min-h-svh flex-col bg-background text-foreground">
			<PublicHeader />
			<main className="relative z-10">
				<div className="mx-auto max-w-6xl px-4 sm:px-6">
					<div className="marketing-line-stack flex flex-col overflow-hidden divide-y divide-border">
						{/* Sidebar + content */}
						<div className="relative overflow-hidden rounded-md bg-white dark:bg-black px-6 py-10 sm:px-10 sm:py-12">
							<div className="relative flex gap-12">
								{/* Sidebar */}
								<aside className="hidden w-52 shrink-0 self-start lg:block">
									<div className="sticky top-6">
										<Sidebar currentSlug={currentSlug} />
									</div>
								</aside>

								{/* Content */}
								<div className="min-w-0 flex-1">
									<details className="lg:hidden mb-6 rounded-md border border-border bg-background/60 group">
										<summary className="flex cursor-pointer items-center justify-between px-4 py-3 text-sm font-medium text-foreground list-none [&::-webkit-details-marker]:hidden">
											<span className="truncate">{currentLabel}</span>
											<span className="ml-2 text-muted-foreground transition-transform group-open:rotate-180">
												&#9662
											</span>
										</summary>
										<div className="border-t border-border px-4 py-4">
											<Sidebar currentSlug={currentSlug} />
										</div>
									</details>
									<Outlet />
								</div>
							</div>
						</div>
					</div>
				</div>
			</main>
			<PublicFooter showCta={false} />
		</div>
	);
}
